import Slider from 'react-slick'
import { useEffect } from 'react'
import { useRef, useState } from 'react'
import { LawTechArticle } from './LawTechArticle'
import { FaCaretRight, FaCaretLeft } from 'react-icons/fa6'
import clsx from 'clsx'

interface Author {
  id: number
  name: string
  bio: string
  author_type: string | null
  avatar: string
  linkedin_url: string | null
}

export interface LawTechArticle {
  id: number
  author: Author
  content: string
  created_at: string
  updated_at: string
  uuid: string
  slug: string
  title: string
  summary: string
  publish_date: string
  cover_image: string
}
interface LawTechSliderProps {
  articles: LawTechArticle[]
  isLoading: boolean
  isLoadingMore: boolean
  articlesCount: number
  loadMore?: (
    isTemplate: boolean,
    pageSize: number,
    isLoadMore: boolean,
  ) => void
}

export function LawTechSlider({
  articles,
  isLoading,
  isLoadingMore,
  articlesCount,
  loadMore,
}: LawTechSliderProps) {
  const sliderRef = useRef<any>(null)
  const [currentSlide, setCurrentSlide] = useState(0)
  const [slidesToShow, setSlidesToShow] = useState(3)
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200,
  )

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const isMobile = windowWidth <= 768

  const settings = {
    dots: false,
    speed: 500,
    swipe: true,
    swipeToSlide: true,
    touchMove: true,
    slidesToShow: isMobile ? 1 : 3,
    slidesToScroll: 1,
    arrows: false,
    infinite: isMobile,
    autoplay: isMobile,
    autoplaySpeed: 3000,
    afterChange: (index: number) => {
      setCurrentSlide(index)
      setSlidesToShow(isMobile ? 1 : 3)
      const isNearEnd = index >= articles.length - (isMobile ? 2 : 4)

      if (isNearEnd && loadMore && !isLoadingMore) {
        loadMore(false, 3, true)
      }
    },
  }

  const totalSlides = articlesCount
  const atStart = currentSlide === 0
  const atEnd = currentSlide >= totalSlides - slidesToShow

  return (
    <div className='w-full relative min-h-[200px]'>
      {isLoading ? (
        <div className='absolute inset-0 z-10 flex items-center justify-center bg-white/60'>
          <div className='w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin'></div>
        </div>
      ) : (
        <>
          <Slider key={windowWidth} ref={sliderRef} {...settings}>
            {articles.map((article, idx) => (
              <div className='p-1'>
                <LawTechArticle
                  key={`${article?.slug}_${idx}`}
                  article={article}
                />
              </div>
            ))}
          </Slider>
          <div className='hidden lg:flex items-center gap-[20px] lg:gap-[35px] self-stretch w-full mt-3 justify-end px-5 lg:px-10'>
            <FaCaretLeft
              className={clsx(
                'text-lg lg:text-3xl cursor-pointer',
                atStart && 'opacity-30 cursor-not-allowed',
              )}
              onClick={() => !atStart && sliderRef.current?.slickPrev()}
            />
            <FaCaretRight
              className={clsx(
                'text-lg lg:text-3xl cursor-pointer',
                atEnd && 'opacity-30 cursor-not-allowed',
              )}
              onClick={() => !atEnd && sliderRef.current?.slickNext()}
            />
          </div>
        </>
      )}
    </div>
  )
}
